import { useCallback, useEffect, useMemo, useState } from 'react';
import { useInfiniteQuery, useQuery } from '@tanstack/react-query';
import type { GridCell } from '@glideapps/glide-data-grid';
import { formatRoundDate } from '@nemo/core/model';
import { chipsCell, type Chip } from './grid/cells/index.js';
import { orderedColumns, type GridColumnDef, type GridLink } from './grid/columns.js';
import { DataGrid } from './grid/DataGrid.js';
import { DensityToggle } from './grid/DensityToggle.js';
import { text } from './grid/fundColumns.js';
import { useGridPreferences } from './grid/useGridPreferences.js';
import { ROUND_SOURCE_LABELS, formatRoundAmount, formatRoundLabel } from './dealroom/format.js';
import { attributedPartner, personHref } from './dealroom/PartnerLink.js';
import { RoundPane } from './dealroom/RoundPane.js';
import type { Round, RoundParticipant } from './dealroom/types.js';
import { useTRPC } from './trpc.js';
import { formatCount, formatDay } from './ui/format.js';
import { Select } from './ui/Select.js';
import { GRID_ICON_TEXT_PADDING } from './ui/SiteIcon.js';

/** How long a person has to stop typing before the search asks the api. */
const SEARCH_DEBOUNCE_MS = 300;

const PREFERENCES_KEY = 'nemo.grid.rounds.v1';

/** Whether the rows are tall enough for a TLDR to take a second line. */
interface RoundsContext {
  readonly twoLine: boolean;
}

/** "Accel (Sonali De Rycker)", or the name alone where the round names nobody. */
function participantText(participant: RoundParticipant): string {
  const label = participant.fundName ?? participant.nameRaw;
  const partner = attributedPartner(participant);
  return partner === null ? label : `${label} (${partner.personName})`;
}

/**
 * Who was in the round as chips: a name matched to a fund we hold is filled,
 * one the feed did not recognise is hollow, and a lead carries the dot.
 */
function participantChips(round: Round): Chip[] {
  return round.participants.map((participant) => ({
    label: participantText(participant),
    tone: 'neutral',
    style: participant.fundId === null ? 'hollow' : undefined,
    mark: participant.lead ? 'lead' : undefined,
  }));
}

/** Each fund we hold that was in the round, and each partner named on it. */
function participantLinks(round: Round): GridLink[] {
  return round.participants.flatMap((participant) => {
    const partner = attributedPartner(participant);
    return [
      ...(participant.fundId === null || participant.fundName === null
        ? []
        : [{ label: participant.fundName, href: `/?fund=${participant.fundId}` }]),
      ...(partner === null
        ? []
        : [
            {
              label: `${partner.personName} (${partner.fundName ?? partner.nameRaw})`,
              href: personHref(partner),
            },
          ]),
    ];
  });
}

function rightAligned(value: string): GridCell {
  return { ...text(value, false), contentAlign: 'right' };
}

const COLUMNS: readonly GridColumnDef<Round, RoundsContext>[] = [
  {
    id: 'company',
    title: 'Company',
    width: 220,
    cell: (round) => ({
      ...text(round.companyName, false),
      cursor: 'pointer',
      // Room for the site icon the grid paints into the cell's left edge.
      themeOverride: { cellHorizontalPadding: GRID_ICON_TEXT_PADDING },
    }),
    text: (round) => round.companyName,
  },
  {
    id: 'tldr',
    title: 'TLDR',
    width: 320,
    cell: (round, context) => text(round.tldr ?? '', false, context.twoLine),
    text: (round) => round.tldr ?? '',
  },
  {
    id: 'round',
    title: 'Round',
    width: 120,
    cell: (round) =>
      chipsCell(
        round.roundLabel === null
          ? []
          : [{ label: formatRoundLabel(round.roundLabel), tone: 'neutral' }],
      ),
    text: (round) => formatRoundLabel(round.roundLabel),
  },
  {
    id: 'sector',
    title: 'Sector',
    width: 140,
    cell: (round) =>
      chipsCell(round.sector === null ? [] : [{ label: round.sector, tone: 'neutral' }]),
    text: (round) => round.sector ?? '',
  },
  {
    id: 'amount',
    title: 'Amount',
    width: 110,
    cell: (round) => rightAligned(formatRoundAmount(round.amountEur)),
    text: (round) => formatRoundAmount(round.amountEur),
  },
  {
    id: 'date',
    title: 'Date',
    width: 100,
    cell: (round) => text(formatRoundDate(round), false),
    text: (round) => formatRoundDate(round),
  },
  {
    id: 'firstSeen',
    title: 'First seen',
    width: 110,
    cell: (round) => text(formatDay(round.firstSeenAt) ?? '', false),
    text: (round) => formatDay(round.firstSeenAt) ?? '',
  },
  {
    id: 'investors',
    title: 'Investors',
    width: 380,
    note: 'Filled: a fund we hold, and a click opens it. Hollow: a name the feed did not recognise. The dot is the lead.',
    cell: (round) => chipsCell(participantChips(round)),
    text: (round) => round.participants.map(participantText).join(', '),
    links: participantLinks,
  },
  {
    id: 'country',
    title: 'Country',
    width: 90,
    cell: (round) => text(round.hqCountries.join(', '), false),
    text: (round) => round.hqCountries.join(', '),
  },
  {
    id: 'source',
    title: 'Source',
    width: 100,
    cell: (round) => text(ROUND_SOURCE_LABELS[round.source], false),
    text: (round) => ROUND_SOURCE_LABELS[round.source],
  },
];

/**
 * The feed itself, newest first, paged from the server as it is scrolled.
 * Every vocabulary the filters offer is read off what has actually arrived, so
 * a filter with nothing behind it is not offered at all rather than offered
 * and always empty. A row opens the round: its company, its press, and every
 * investor in it as a way into that fund.
 */
export function RoundsPage(): React.JSX.Element {
  const trpc = useTRPC();
  const filters = useQuery(trpc.rounds.filters.queryOptions());

  const [sector, setSector] = useState('');
  const [roundLabel, setRoundLabel] = useState('');
  const [country, setCountry] = useState('');
  const [since, setSince] = useState('');
  const [searchInput, setSearchInput] = useState('');
  const [search, setSearch] = useState('');
  const [openId, setOpenId] = useState<string | null>(null);
  const preferences = useGridPreferences(PREFERENCES_KEY);
  const columns = useMemo(() => orderedColumns(COLUMNS, preferences), [preferences]);
  const twoLine = preferences.density === 'comfortable';
  const context = useMemo(() => ({ twoLine }), [twoLine]);

  // A round matches on the company or an investor's name; asking the api on
  // every keystroke would just make it answer the wrong question repeatedly.
  useEffect(() => {
    const timer = window.setTimeout(() => setSearch(searchInput.trim()), SEARCH_DEBOUNCE_MS);
    return () => window.clearTimeout(timer);
  }, [searchInput]);

  const narrowed =
    sector !== '' || roundLabel !== '' || country !== '' || since !== '' || search !== '';

  const rounds = useInfiniteQuery(
    trpc.rounds.list.infiniteQueryOptions(
      {
        ...(sector === '' ? {} : { sectors: [sector] }),
        ...(roundLabel === '' ? {} : { roundLabels: [roundLabel] }),
        ...(country === '' ? {} : { countries: [country] }),
        ...(since === '' ? {} : { since }),
        ...(search === '' ? {} : { text: search }),
      },
      { getNextPageParam: (page) => page.nextCursor },
    ),
  );

  const rows: readonly Round[] = useMemo(
    () => (rounds.data?.pages ?? []).flatMap((page) => page.rounds),
    [rounds.data],
  );
  const total = rounds.data?.pages[0]?.total ?? null;
  const openRound = openId === null ? undefined : rows.find((round) => round.id === openId);
  const closePane = useCallback(() => setOpenId(null), []);

  if (rounds.isError) {
    return <p className="subtle">Could not load the rounds: {rounds.error.message}</p>;
  }

  return (
    <div className="page">
      <div className="toolbar">
        <strong className="count">
          {rounds.isPending ? 'Loading the rounds…' : `${formatCount(total ?? rows.length)} rounds`}
        </strong>

        <input
          className="search"
          placeholder="Company or investor…"
          aria-label="Search the rounds"
          value={searchInput}
          onChange={(event) => setSearchInput(event.target.value)}
        />

        {(filters.data?.sectors ?? []).length > 0 && (
          <Select
            label="Sector"
            value={sector}
            onChange={setSector}
            options={[
              { value: '', label: 'Every sector' },
              ...(filters.data?.sectors ?? []).map((name) => ({ value: name, label: name })),
            ]}
          />
        )}

        {(filters.data?.roundLabels ?? []).length > 0 && (
          <Select
            label="Round"
            value={roundLabel}
            onChange={setRoundLabel}
            options={[
              { value: '', label: 'Every round' },
              ...(filters.data?.roundLabels ?? []).map((label) => ({
                value: label,
                label: formatRoundLabel(label),
              })),
            ]}
          />
        )}

        {(filters.data?.countries ?? []).length > 0 && (
          <Select
            label="Country"
            value={country}
            onChange={setCountry}
            options={[
              { value: '', label: 'Everywhere' },
              ...(filters.data?.countries ?? []).map((code) => ({ value: code, label: code })),
            ]}
          />
        )}

        <label className="since">
          Since
          <input type="date" value={since} onChange={(event) => setSince(event.target.value)} />
        </label>

        {narrowed && (
          <button
            className="button"
            type="button"
            onClick={() => {
              setSector('');
              setRoundLabel('');
              setCountry('');
              setSince('');
              setSearchInput('');
              setSearch('');
            }}
          >
            Clear filters
          </button>
        )}
        <span className="spacer" />
        <DensityToggle density={preferences.density} onChange={preferences.setDensity} />
      </div>

      <DataGrid
        rows={rows}
        columns={columns}
        context={context}
        rowId={(round) => round.id}
        preferences={preferences}
        paging={{
          hasMore: rounds.hasNextPage,
          loading: rounds.isFetchingNextPage,
          onReachEnd: () => void rounds.fetchNextPage(),
        }}
        siteIcon={{
          columnId: 'company',
          of: (round) => ({ website: round.companyWebsite, name: round.companyName }),
        }}
        open={{
          openId,
          onOpen: (round) => setOpenId(round.id),
          onClose: closePane,
          chevronTip: 'Open this round — click here, its company, or press Enter on the row.',
        }}
        tip={tipFor}
        freezeColumns={1}
        empty={
          rounds.isPending ? null : (
            <span className="subtle">
              {narrowed ? 'No rounds match those filters.' : 'No rounds have come in yet.'}
            </span>
          )
        }
        aside={(step) =>
          openRound !== undefined && (
            <RoundPane round={openRound} onClose={closePane} onStep={step} />
          )
        }
      />
    </div>
  );
}

/** The whole of what a narrow cell cuts short. */
function tipFor(round: Round, column: GridColumnDef<Round, RoundsContext>): string | null {
  if (column.id === 'tldr') return round.tldr;
  if (column.id === 'investors' && round.participants.length > 0) {
    return round.participants
      .map((participant) => `${participantText(participant)}${participant.lead ? ' · lead' : ''}`)
      .join('\n');
  }
  return null;
}
